import React, { useMemo, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import './orgchart.css';

// Une couleur distincte par département
const DEPT_PALETTE = [
  { line: '#0f766e', border: '#0f766e', bg: '#ccfbf1', text: '#134e4a' },
  { line: '#1d4ed8', border: '#1d4ed8', bg: '#dbeafe', text: '#1e3a8a' },
  { line: '#b45309', border: '#b45309', bg: '#fef3c7', text: '#78350f' },
  { line: '#7e22ce', border: '#7e22ce', bg: '#f3e8ff', text: '#581c87' },
  { line: '#be123c', border: '#be123c', bg: '#ffe4e6', text: '#881337' },
  { line: '#15803d', border: '#15803d', bg: '#dcfce7', text: '#14532d' },
  { line: '#0369a1', border: '#0369a1', bg: '#e0f2fe', text: '#0c4a6e' },
  { line: '#a16207', border: '#a16207', bg: '#fef9c3', text: '#713f12' },
  { line: '#4338ca', border: '#4338ca', bg: '#e0e7ff', text: '#312e81' },
  { line: '#c2410c', border: '#c2410c', bg: '#ffedd5', text: '#7c2d12' },
  { line: '#0e7490', border: '#0e7490', bg: '#cffafe', text: '#164e63' },
  { line: '#a21caf', border: '#a21caf', bg: '#fae8ff', text: '#701a75' },
];

const MANAGER_ROLES = ['admin', 'super_admin', 'lead_ops', 'lead_field_ops', 'lead_commercial'];

// Direction générale : affichée au-dessus des départements
const DIRECTION_DEPTS = ['comex', 'direction', 'direction générale', 'direction generale'];
const DIRECTION_TITLE_RE = /^(ceo|pdg|p-dg|président|presidente|présidente|directeur général|directrice générale|directeur general|gérant|gerant|managing director|general manager)/i;

const paletteVars = (p) => ({
  '--org-line': p.line,
  '--node-border': p.border,
  '--node-bg': p.bg,
  '--node-text': p.text,
});

const PersonNode = ({ person, childrenByManager, isManager }) => {
  const [open, setOpen] = useState(true);
  const children = childrenByManager[person.id] || [];

  return (
    <li>
      <div
        className={`org-node ${person.isActive === false ? 'org-node--inactive' : ''}`}
        data-testid={`org-person-${person.id}`}
      >
        <p className="org-node-name">
          {person.firstName} {person.lastName}
        </p>
        <p className={`org-node-role ${person.jobTitle ? '' : 'org-node-role--empty'}`}>
          {person.jobTitle || 'Poste à renseigner'}
        </p>
        {isManager && (
          <span className="org-badge" data-testid={`org-manager-badge-${person.id}`}>
            Responsable
          </span>
        )}
        {children.length > 0 && (
          <div>
            <button
              type="button"
              className="org-collapse"
              onClick={() => setOpen(!open)}
              data-testid={`org-toggle-${person.id}`}
            >
              {open ? '−' : '+'} {children.length} personne{children.length > 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>

      {open && children.length > 0 && (
        <ul>
          {children.map((child) => (
            <PersonNode key={child.id} person={child} childrenByManager={childrenByManager} />
          ))}
        </ul>
      )}
    </li>
  );
};

const DepartmentNode = ({ dept }) => {
  const [open, setOpen] = useState(true);

  return (
    <li style={paletteVars(dept.palette)}>
      <div className="org-node org-node--dept" data-testid={`org-dept-${dept.name}`}>
        <p className="org-node-name">{dept.name}</p>
        <p className="org-node-role">
          {dept.members.length} personne{dept.members.length > 1 ? 's' : ''}
        </p>
        <button
          type="button"
          className="org-collapse"
          onClick={() => setOpen(!open)}
          data-testid={`org-dept-toggle-${dept.name}`}
        >
          {open ? '− replier' : '+ déplier'}
        </button>
      </div>

      {open && dept.roots.length > 0 && (
        <ul>
          {dept.roots.map((person) => (
            <PersonNode
              key={person.id}
              person={person}
              childrenByManager={dept.childrenByManager}
              isManager={dept.managerIds.includes(person.id)}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const OrgChart = ({ users = [], companyName = 'Baobab Connect' }) => {
  const scrollRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/internal-users/org-chart/pdf`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'organigramme-baobab-connect.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success('Organigramme téléchargé');
    } catch (e) {
      toast.error("Export impossible pour le moment");
    } finally {
      setExporting(false);
    }
  };

  const { direction, departments } = useMemo(() => {
    const byId = Object.fromEntries(users.map((u) => [u.id, u]));
    const deptOf = (u) => ((u.departement || '').trim() || 'Sans département');
    const sortFn = (a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    const managerOf = (u) => {
      const mgr = u.manager_id ? byId[u.manager_id] : null;
      return mgr && mgr.id !== u.id ? mgr : null;
    };

    // Direction générale : personnes sans responsable, au département de direction
    // ou portant un poste de direction. Elles coiffent tout l'organigramme.
    const directionPeople = users
      .filter((u) => !managerOf(u) && (
        DIRECTION_DEPTS.includes(deptOf(u).toLowerCase())
        || DIRECTION_TITLE_RE.test((u.jobTitle || '').trim())
      ))
      .sort(sortFn);
    const directionIds = new Set(directionPeople.map((u) => u.id));

    const groups = {};
    users.filter((u) => !directionIds.has(u.id)).forEach((u) => {
      (groups[deptOf(u)] = groups[deptOf(u)] || []).push(u);
    });

    const depts = Object.entries(groups)
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .map(([name, members], i) => {
        // Rattachements explicites, limités au département
        const childrenByManager = {};
        members.forEach((m) => {
          const mgr = managerOf(m);
          if (mgr && deptOf(mgr) === name && !directionIds.has(mgr.id)) {
            (childrenByManager[mgr.id] = childrenByManager[mgr.id] || []).push(m);
          }
        });

        let roots = members
          .filter((m) => {
            const mgr = managerOf(m);
            return !mgr || deptOf(mgr) !== name || directionIds.has(mgr.id);
          })
          .sort(sortFn);

        // Responsables : rattachement explicite d'une équipe, ou rattachement direct
        // à la direction générale, sinon les profils d'encadrement
        let managerIds = roots
          .filter((m) => (childrenByManager[m.id] || []).length > 0
            || directionIds.has(m.manager_id))
          .map((m) => m.id);

        if (managerIds.length === 0) {
          const leads = roots.filter((m) => MANAGER_ROLES.includes((m.role || '').toLowerCase()));
          if (leads.length > 0 && leads.length < roots.length) {
            // L'équipe est présentée sous son responsable
            const leadIds = leads.map((l) => l.id);
            const team = roots.filter((m) => !leadIds.includes(m.id));
            team.forEach((m) => {
              (childrenByManager[leads[0].id] = childrenByManager[leads[0].id] || []).push(m);
            });
            roots = leads;
            managerIds = [leads[0].id];
          } else if (leads.length > 0) {
            managerIds = leads.map((l) => l.id);
          }
        }

        Object.values(childrenByManager).forEach((list) => list.sort(sortFn));

        return {
          name,
          members,
          roots,
          childrenByManager,
          managerIds,
          palette: DEPT_PALETTE[i % DEPT_PALETTE.length],
        };
      });

    return { direction: directionPeople, departments: depts };
  }, [users]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    }
  }, [users]);

  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500 text-sm">
          Aucun membre du personnel à afficher.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="org-chart">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3" data-testid="org-legend">
          {departments.map((d) => (
            <span key={d.name} className="text-xs text-gray-600">
              <i className="org-legend-dot" style={{ background: d.palette.border }} />
              {d.name} ({d.members.length})
            </span>
          ))}
          <button
            type="button"
            onClick={exportPdf}
            disabled={exporting}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
            data-testid="org-export-pdf-btn"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? 'Génération…' : 'Exporter en PDF'}
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-2">
          Entreprise → direction générale → départements → responsable → équipe. Le rattachement
          d&apos;une personne (« qui reporte à qui ») se règle avec l&apos;icône
          « Manager / Équipe » de la liste du personnel, ou via le champ « Responsable
          hiérarchique » de sa fiche. À défaut, les profils d&apos;encadrement sont présentés
          comme responsables de leur département.
        </p>
        <div className="overflow-x-auto pb-2" ref={scrollRef}>
          <div className="orgtree">
            <ul style={{ paddingTop: 0 }}>
              <li>
                <div className="org-node org-node--root" data-testid="org-root">
                  <p className="org-node-name flex items-center justify-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" />
                    {companyName}
                  </p>
                  <p className="org-node-role">
                    {users.length} personne{users.length > 1 ? 's' : ''} ·{' '}
                    {departments.length} département{departments.length > 1 ? 's' : ''}
                  </p>
                </div>
                <ul>
                  {direction.length > 0 ? (
                    direction.map((person, i) => (
                      <li key={person.id}>
                        <div
                          className="org-node org-node--direction"
                          data-testid={`org-direction-${person.id}`}
                        >
                          <p className="org-node-name">
                            {person.firstName} {person.lastName}
                          </p>
                          <p className={`org-node-role ${person.jobTitle ? '' : 'org-node-role--empty'}`}>
                            {person.jobTitle || 'Poste à renseigner'}
                          </p>
                          <span className="org-badge">Direction générale</span>
                        </div>
                        {i === 0 && departments.length > 0 && (
                          <ul>
                            {departments.map((dept) => (
                              <DepartmentNode key={dept.name} dept={dept} />
                            ))}
                          </ul>
                        )}
                      </li>
                    ))
                  ) : (
                    departments.map((dept) => (
                      <DepartmentNode key={dept.name} dept={dept} />
                    ))
                  )}
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OrgChart;
